import type { ContextStylePane } from 'configuration-schemas/confdefn';
import { isListDataApi } from 'configuration-schemas/confdefnView';
import { AppType } from 'configuration-schemas/general';
import type { ListDataOptions } from 'src/worker/pivotWorker.types';

export interface StylePaneable {
  id: string;
  parentId: string;
}

/** Identifies the Style Pane as the `sourceComponent` on `requestExternalDataRefresh` payloads.
 * Used both when the pane emits (so host views redecorate) and in the pane's own receiving epic
 * (so `isNotSameExternalDataRefreshRequest` filters out the pane's own edits and it doesn't
 * redecorate itself). Both sides must use this same value.
 */
export const STYLE_PANE_COMPONENT = 'StylePane';

/** Supported hardcoded `StylePane` modeldefn ids
 * These are the only two supported, as support for a second
 * defnId was glued on when Assortment Build was added
 * when dinosaurs walked the earth
 */
export const enum StylePaneDefnIds {
  HistoryStyleReview = 'HistoryStyleReview',
  StyleChannelReview = 'StyleChannelReview',
}

/** Ancient hardcoding */
export const stylePaneHardcodedAggBys = ['level:channel', 'level:stylecolor'];

/** These are also ancient hardcoding that has always existed
 * Exported here so it can remain consistent
 */
export const getStylePaneViewDefnId = (isAssortmentBuild: boolean) =>
  isAssortmentBuild ? 'AssortmentStyleDetailsPane' : 'HistoryStylePaneLayout';
export const getStylePaneDefnId = (isAssortmentBuild: boolean) =>
  isAssortmentBuild ? StylePaneDefnIds.StyleChannelReview : StylePaneDefnIds.HistoryStyleReview;

export interface ResolvedStylePaneConfig {
  viewDefnId: string;
  appName: AppType;
  dataDefnId: string;
  dataParams: ListDataOptions;
  aggBy: string[];
}

/** Resolve the context configuration, retaining the two legacy definitions as fallbacks. */
export const resolveStylePaneConfig = (
  stylePane: ContextStylePane | undefined,
  isAssortmentBuild: boolean,
): ResolvedStylePaneConfig => {
  const legacyDataDefnId = getStylePaneDefnId(isAssortmentBuild);
  const configuredDataDefnId = isListDataApi(stylePane?.dataApi)
    ? stylePane.dataApi.defnId
    : stylePane?.dataApi.params?.defnId;
  const dataParams = (stylePane?.dataApi.params ?? {}) as ListDataOptions;
  const aggBy = typeof dataParams.aggBy === 'string' ? dataParams.aggBy.split(',') : stylePaneHardcodedAggBys;

  return {
    viewDefnId: stylePane?.configApi.params.defnId ?? getStylePaneViewDefnId(isAssortmentBuild),
    appName: stylePane?.configApi.params.appName ?? AppType.Assortment,
    dataDefnId: configuredDataDefnId ?? legacyDataDefnId,
    dataParams,
    aggBy,
  };
};
